import { ApiSchemas } from '@/components/providers/v1-api';

type ExternalCallMetric = ApiSchemas['ExternalCallMetric'];

export type MetricKind = 'boolean' | 'number' | 'string' | 'list' | 'empty';

export interface MetricStatusBase {
  label?: string;
  kind: MetricKind;
  value: boolean | number | string | string[] | null;
  isEmpty: boolean;
}

export interface BooleanMetricStatus extends MetricStatusBase {
  kind: 'boolean';
  value: boolean | null;
  truthy: boolean | null;
  color: string; // tailwind class for background/text
}

export interface NumberMetricStatus extends MetricStatusBase {
  kind: 'number';
  value: number | null;
  min: number;
  max: number;
  // 0..1 normalized (null if cannot compute)
  normalized: number | null;
  percent: number | null; // 0..100
  severity: 'low' | 'medium' | 'high' | null;
  barColor: string; // tailwind class
}

export interface StringMetricStatus extends MetricStatusBase {
  kind: 'string';
  value: string | null;
}

export interface ListMetricStatus extends MetricStatusBase {
  kind: 'list';
  value: string[];
  count: number;
}

export interface EmptyMetricStatus extends MetricStatusBase {
  kind: 'empty';
  value: null;
}

export type MetricStatus =
  | BooleanMetricStatus
  | NumberMetricStatus
  | StringMetricStatus
  | ListMetricStatus
  | EmptyMetricStatus;

// Map numeric value into severity buckets (basic heuristic) so that multiple
// UI components can re-use the color decision logic.
function numberSeverity(normalized: number | null): 'low' | 'medium' | 'high' | null {
  if (normalized == null) return null;
  if (normalized < 0.33) return 'low';
  if (normalized < 0.66) return 'medium';
  return 'high';
}

function severityColor(sev: 'low' | 'medium' | 'high' | null) {
  switch (sev) {
    case 'low':
      return 'bg-red-500/70';
    case 'medium':
      return 'bg-amber-500/70';
    case 'high':
      return 'bg-green-500/70';
    default:
      return 'bg-primary/70';
  }
}

export function buildMetricStatus(metrics: ExternalCallMetric | undefined): MetricStatus {
  const raw = metrics;
  if (!raw || !raw.value) {
    return {
      // key,
      label: raw?.label,
      kind: 'empty',
      value: null,
      isEmpty: true,
    } as EmptyMetricStatus;
  }

  const { value } = raw;
  switch (value.type) {
    case 'boolean': {
      const val = value.b ?? null;
      return {
        // key,
        label: raw.label,
        kind: 'boolean',
        value: val,
        truthy: val,
        isEmpty: val === null,
        color: val === null ? 'bg-muted' : val ? 'bg-green-600 text-white' : 'bg-gray-400 text-white',
      } as BooleanMetricStatus;
    }
    case 'number': {
      const n = value.n ?? null;
      const min = raw.min ?? 0;
      const max = raw.max ?? 1;
      const normalized = n == null || max === min ? null : (n - min) / (max - min);
      const percent = normalized == null ? null : normalized * 100;
      const severity = numberSeverity(normalized);
      return {
        // key,
        label: raw.label,
        kind: 'number',
        value: n,
        min,
        max,
        normalized,
        percent,
        severity,
        barColor: severityColor(severity),
        isEmpty: n == null,
      } as NumberMetricStatus;
    }
    case 'string': {
      const s = value.s ?? null;
      return {
        // key,
        label: raw.label,
        kind: 'string',
        value: s,
        isEmpty: !s,
      } as StringMetricStatus;
    }
    case 'list_of_strings': {
      const list = value.ss ?? [];
      return {
        // key,
        label: raw.label,
        kind: 'list',
        value: list,
        count: list.length,
        isEmpty: list.length === 0,
      } as ListMetricStatus;
    }
    default:
      return {
        // key,
        label: raw.label,
        kind: 'empty',
        value: null,
        isEmpty: true,
      } as EmptyMetricStatus;
  }
}

// Convenience: produce a React-friendly (string) fallback display
export function metricStatusText(status: MetricStatus): string {
  if (status.isEmpty) return '—';
  switch (status.kind) {
    case 'boolean':
      return status.value == null ? '—' : status.value ? 'Yes' : 'No';
    case 'number':
      return status.value == null ? '—' : String(status.value);
    case 'string':
      return status.value || '—';
    case 'list':
      return status.value.join(', ');
    default:
      return '—';
  }
}
